import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import AffiliationModalStatus from "./AffiliationModalStatus";
import AffiliationService from "../../services/AffiliationService";
import { getMinDateTimeLocal, isFutureDate, toFutureDateTimeLocal } from "./promocodeDate";
import "../AdminPages/AdminModalForm.css";

const AffiliationPromocodeStatusModal = ({ isOpen, action, promocode, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const [dateEnd, setDateEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const isActivating = action === "activate";

  useEffect(() => {
    if (!isOpen || !promocode) return;
    setDateEnd(toFutureDateTimeLocal(promocode.date_end));
    setError("");
    setSuccess("");
  }, [isOpen, promocode]);

  const handleSubmit = async () => {
    if (saving || !promocode) return;

    let dateEndIso;
    if (isActivating) {
      const parsedDate = new Date(dateEnd);
      if (!dateEnd || Number.isNaN(parsedDate.getTime()) || !isFutureDate(parsedDate)) {
        setError(t("affiliation.promocodeDateMustBeFuture", "Expiration date must be in the future."));
        return;
      }
      dateEndIso = parsedDate.toISOString();
    }

    try {
      setSaving(true);
      setError("");
      if (isActivating) {
        await AffiliationService.activateAffiliatePromocode(promocode.code, dateEndIso);
      } else {
        await AffiliationService.disableAffiliatePromocode(promocode.code);
      }
      setSuccess(
        isActivating
          ? t("affiliation.promocodeActivatedSuccessfully", "Promocode activated successfully.")
          : t("affiliation.promocodeDisabledSuccessfully", "Promocode disabled successfully."),
      );
      onCompleted?.();
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : requestError.message || t("errors.generic", "Something went wrong"),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !promocode) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={
        isActivating
          ? t("affiliation.activatePromocode", "Activate Promocode")
          : t("affiliation.disablePromocode", "Disable Promocode")
      }
      actions={
        success ? (
          <MainButton onClick={onClose}>{t("common.close", "Close")}</MainButton>
        ) : (
          <>
            <SecondButton onClick={onClose} disabled={saving}>
              {t("common.cancel", "Cancel")}
            </SecondButton>
            <MainButton onClick={handleSubmit} disabled={saving}>
              {saving
                ? t("common.saving", "Saving...")
                : isActivating
                  ? t("affiliation.activate", "Activate")
                  : t("affiliation.disable", "Disable")}
            </MainButton>
          </>
        )
      }
    >
      <div className="admin-modal-form">
        <p>
          {isActivating
            ? t("affiliation.confirmActivatePromocode", "Activate this promocode:")
            : t("affiliation.confirmDisablePromocode", "Disable this promocode?")}
          <br />
          <strong>{promocode.code}</strong>
        </p>
        {isActivating && (
          <label>
            {t("affiliation.promocodeExpiresAt", "Expires at")} *
            <MainInput
              type="datetime-local"
              value={dateEnd}
              onChange={(event) => {
                setDateEnd(event.target.value);
                setError("");
              }}
              required
              disabled={saving || Boolean(success)}
              min={getMinDateTimeLocal()}
            />
          </label>
        )}
        <AffiliationModalStatus
          error={error}
          success={success}
          onErrorClose={() => setError("")}
          onSuccessClose={onClose}
        />
      </div>
    </Modal>
  );
};

export default AffiliationPromocodeStatusModal;
